"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Image from "@/components/shared/image";
import { useAuth } from "@/components/auth/AuthProvider";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  getHomepageMotionEnvironment,
  shouldUseHeavyHomepageMotion,
} from "@/components/LandingPage/mobile-motion-policy";
import { getActiveLenis } from "@/components/providers/lenis-instance";
import { LANGUAGE_FLAGS, type LanguageFlagItem } from "@/lib/languages_and_flag";
import { useAnalytics } from "@/lib/analytics";
import { useColorTheme } from "@/components/providers/color-theme-provider";
import { cn } from "@/lib/utils";

const GRID_DESKTOP = {
  rows: 9,
  cols: 9,
  centerRow: 4,
  centerColStart: 2,
  centerColSpan: 5,
} as const;

/** Phone: 5 columns (→) × 9 rows (↓); “19+ Languages” spans full width on the middle row. */
const GRID_PHONE = {
  rows: 9,
  cols: 5,
  centerRow: 4,
  centerColStart: 0,
  centerColSpan: 5,
} as const;

type LanguageGridCell =
  | { key: string; kind: "center"; row: number }
  | { key: string; kind: "flag"; item: LanguageFlagItem; highlight: boolean; row: number };

function buildLanguageGridCells(
  supportedFlags: LanguageFlagItem[],
  mutedFlags: LanguageFlagItem[],
  opts: {
    rows: number;
    cols: number;
    centerRow: number;
    centerColStart: number;
    centerColSpan: number;
    keyPrefix: string;
  },
): LanguageGridCell[] {
  const {
    rows: ROWS,
    cols: COLS,
    centerRow: CENTER_ROW,
    centerColStart: CENTER_COL_START,
    centerColSpan: CENTER_COL_SPAN,
    keyPrefix,
  } = opts;
  const pk = `${keyPrefix}-`;

  let mutedCursor = 0;
  let supportedCursor = 0;
  const totalFlagSlots = ROWS * COLS - CENTER_COL_SPAN;
  const mutedCount = mutedFlags.length;
  const supportedCount = Math.max(0, totalFlagSlots - mutedCount);
  const randomizedSlotOrder = Array.from({ length: totalFlagSlots }, (_, i) => i).sort((a, b) => {
    const sa = Math.sin((a + 1) * 91.137 + 17.13);
    const sb = Math.sin((b + 1) * 91.137 + 17.13);
    return sa - sb;
  });
  const highlightedSlotSet = new Set(randomizedSlotOrder.slice(0, supportedCount));
  let flagSlotCursor = 0;

  const gridCells: LanguageGridCell[] = [];

  for (let row = 0; row < ROWS; row += 1) {
    for (let col = 0; col < COLS; col += 1) {
      const inCenterTextCell =
        row === CENTER_ROW && col >= CENTER_COL_START && col < CENTER_COL_START + CENTER_COL_SPAN;

      if (inCenterTextCell) {
        if (col === CENTER_COL_START) {
          gridCells.push({ key: `${pk}center-${row}-${col}`, kind: "center", row });
        }
        continue;
      }

      const useHighlight = highlightedSlotSet.has(flagSlotCursor);
      const item = useHighlight
        ? supportedFlags[supportedCursor++ % supportedFlags.length]!
        : mutedFlags[mutedCursor++ % mutedFlags.length]!;
      const highlight = useHighlight;
      flagSlotCursor += 1;

      gridCells.push({ key: `${pk}flag-${row}-${col}`, kind: "flag", item, highlight, row });
    }
  }

  return gridCells;
}

// Only 2 flags will be shown as uncolored (grayscale)
const UNCOLORED_COUNTRY_CODES = new Set([
  "CH", // Switzerland - uncolored
  "SG", // Singapore - uncolored
]);

export default function LanguageSupport() {
  const { openAuth } = useAuth();
  const { track } = useAnalytics();
  const { themeColor } = useColorTheme();
  const sectionRef = useRef<HTMLElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const planeMoveRef = useRef<HTMLDivElement>(null);
  const planeImgRef = useRef<HTMLImageElement>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const popoverRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const activePopoverKeyRef = useRef<string | null>(null);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isClientMounted, setIsClientMounted] = useState(false);
  const [requestLanguage, setRequestLanguage] = useState("");
  const [requestLanguageFlag, setRequestLanguageFlag] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [requestFeedback, setRequestFeedback] = useState("");
  const REGISTERED_EMAIL = "hello@zapify.ai";

  // Most flags colored, only 2-3 uncolored
  const supportedFlags = LANGUAGE_FLAGS.filter(
    (item) => !UNCOLORED_COUNTRY_CODES.has(item.country_code),
  );
  const mutedFlags = LANGUAGE_FLAGS.filter(
    (item) => UNCOLORED_COUNTRY_CODES.has(item.country_code),
  );

  const gridCellsPhone = buildLanguageGridCells(supportedFlags, mutedFlags, {
    ...GRID_PHONE,
    keyPrefix: "m",
  });
  const gridCellsDesktop = buildLanguageGridCells(supportedFlags, mutedFlags, {
    ...GRID_DESKTOP,
    keyPrefix: "d",
  });

  useLayoutEffect(() => {
    if (!shouldUseHeavyHomepageMotion(getHomepageMotionEnvironment())) return;

    gsap.registerPlugin(ScrollTrigger);
    const section = sectionRef.current;
    const shell = shellRef.current;
    if (!section || !shell) return;

    // Use lg (1024px) so landscape phones still get 5×9; md would switch to 9×9 too early.
    const isDesktop =
      typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches;
    const activeShell = section.querySelector<HTMLElement>(
      `[data-language-grid="${isDesktop ? "desktop" : "phone"}"]`,
    );
    if (!activeShell) return;

    const allFlags = Array.from(activeShell.querySelectorAll<HTMLElement>("[data-language-flag]"));
    const supportedFlagEls = Array.from(activeShell.querySelectorAll<HTMLElement>("[data-supported-flag='true']"));
    const centerCard = activeShell.querySelector<HTMLElement>("[data-language-center]");

    if (!allFlags.length || !centerCard) return;
    const ctx = gsap.context(() => {
      gsap.set(shell, { maxWidth: "64rem" });
      gsap.set(allFlags, { opacity: 0, y: 12, scale: 0.98, filter: "grayscale(1) saturate(0.2)" });
      gsap.set(centerCard, { opacity: 0, y: 10, scale: 0.98 });

      const planeMove = planeMoveRef.current;
      const planeImg = planeImgRef.current;
      if (planeMove && planeImg) {
        const shadowSoft =
          "drop-shadow(3px 4px 7px rgba(0,0,0,0.06)) drop-shadow(1px 2px 4px rgba(0,0,0,0.05))";
        const shadowHard =
          "drop-shadow(6px 8px 12px rgba(0,0,0,0.16)) drop-shadow(3px 5px 8px rgba(0,0,0,0.1))";
        gsap.set(planeMove, {
          left: "86%",
          top: "8%",
          xPercent: -50,
          yPercent: -50,
          force3D: true,
        });
        gsap.set(planeImg, {
          filter: shadowSoft,
          rotation: 0,
          force3D: true,
        });

        gsap
          .timeline({
            scrollTrigger: {
              trigger: section,
              start: "top 78%",
              once: true,
              onEnter: () => getActiveLenis()?.resize(),
            },
            onComplete: () => {
              getActiveLenis()?.resize();
              ScrollTrigger.refresh();
              gsap.set(planeMove, {
                left: "0%",
                top: "103%",
                xPercent: 0,
                yPercent: 0,
                clearProps: "will-change",
              });
              gsap.set(planeImg, { clearProps: "will-change" });
            },
          })
          .to(
            planeMove,
            {
              left: "0%",
              top: "103%",
              xPercent: 0,
              yPercent: 0,
              duration: 9.25,
              ease: "power1.inOut",
            },
            0,
          )
          .to(
            planeImg,
            {
              filter: shadowHard,
              duration: 5.75,
              ease: "power1.inOut",
            },
            ">-3.25",
          );
      }

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top 82%",
          once: true,
          onEnter: () => getActiveLenis()?.resize(),
        },
      });

      tl.to(shell, {
        maxWidth: "80rem",
        duration: 0.9,
        ease: "power3.out",
        onComplete: () => ScrollTrigger.refresh(),
      })
        .to(centerCard, { opacity: 1, y: 0, scale: 1, duration: 0.45, ease: "power3.out" }, "-=0.62")
        .to(
          allFlags,
          {
            opacity: 0.5,
            y: 0,
            scale: 1,
            duration: 0.4,
            stagger: {
              each: 0.007,
              from: "random",
            },
            ease: "power2.out",
          },
          "-=0.4",
        )
        .to(
          supportedFlagEls,
          {
            opacity: 1,
            filter: "grayscale(0) saturate(1)",
            y: -1,
            scale: 1.08,
            duration: 0.45,
            stagger: 0.06,
            ease: "back.out(1.45)",
          },
          "-=0.08",
        )
        .to(
          supportedFlagEls,
          {
            y: 0,
            scale: 1,
            duration: 0.32,
            ease: "power2.out",
          },
          "-=0.1",
        );

      if (supportedFlagEls.length) {
        gsap.to(supportedFlagEls, {
          y: -2,
          scale: 1.05,
          duration: 1.35,
          stagger: 0.08,
          ease: "sine.inOut",
          yoyo: true,
          repeat: -1,
        });
      }
    }, section);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    setIsClientMounted(true);
    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const lenis = getActiveLenis();
    if (!lenis) return;
    if (isRequestModalOpen) {
      lenis.stop();
      return;
    }
    lenis.start();
  }, [isRequestModalOpen]);

  const animatePopoverIn = (cellKey: string) => {
    const popover = popoverRefs.current[cellKey];
    if (!popover) return;
    gsap.killTweensOf(popover);
    gsap.set(popover, { pointerEvents: "auto", visibility: "visible" });
    gsap.to(popover, {
      autoAlpha: 1,
      y: 0,
      duration: 0.16,
      ease: "power2.out",
    });
  };

  const animatePopoverOut = (cellKey: string, immediate = false) => {
    const popover = popoverRefs.current[cellKey];
    if (!popover) return;
    gsap.killTweensOf(popover);
    gsap.to(popover, {
      autoAlpha: 0,
      y: 6,
      duration: immediate ? 0.1 : 0.22,
      ease: immediate ? "power2.out" : "power2.inOut",
      onStart: () => {
        gsap.set(popover, { pointerEvents: "none" });
      },
      onComplete: () => {
        gsap.set(popover, { visibility: "hidden" });
      },
    });
  };

  const handlePopoverEnter = (cellKey: string) => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    const previousKey = activePopoverKeyRef.current;
    if (previousKey && previousKey !== cellKey) {
      animatePopoverOut(previousKey, true);
    }
    activePopoverKeyRef.current = cellKey;
    animatePopoverIn(cellKey);
  };

  const handlePopoverLeave = (cellKey: string) => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
    if (activePopoverKeyRef.current !== cellKey) return;
    hideTimerRef.current = setTimeout(() => {
      if (activePopoverKeyRef.current !== cellKey) return;
      activePopoverKeyRef.current = null;
      animatePopoverOut(cellKey);
    }, 3000);
  };

  const openRequestModal = (languageName: string, flagUrl: string) => {
    setIsRequestModalOpen(true);
    setRequestLanguage(languageName);
    setRequestLanguageFlag(flagUrl);
    setEmailInput("");
    setRequestFeedback("");
    if (activePopoverKeyRef.current) {
      animatePopoverOut(activePopoverKeyRef.current, true);
    }
    activePopoverKeyRef.current = null;
  };

  const closeRequestModal = () => {
    setIsRequestModalOpen(false);
    setRequestFeedback("");
  };

  const handleRequestSubmit = () => {
    const normalizedEmail = emailInput.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setRequestFeedback("Please enter a valid email address.");
      return;
    }
    if (normalizedEmail !== REGISTERED_EMAIL) {
      setRequestFeedback("This is not your registered email.");
      return;
    }
    setRequestFeedback(`Request submitted for ${requestLanguage}. We will notify you soon.`);
  };

  const hasEmailInput = emailInput.trim().length > 0;

  const mapLanguageCells = (cells: LanguageGridCell[]) =>
    cells.map((cell) => {
      if (cell.kind === "center") {
        return (
          <div
            key={cell.key}
            data-language-center
            className="relative z-30 col-span-5 flex items-center justify-center"
          >
            <div className="rounded-lg bg-white px-6 py-2 dark:bg-black">
              <p className="whitespace-nowrap text-center text-2xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                Built for creators worldwide
              </p>
            </div>
          </div>
        );
      }

      const item = cell.item;
      const isHighlightSlot = cell.highlight;
      // Popover disabled - no longer used
      const showRequestPopover = false;

      return (
        <div
          key={cell.key}
          className={`group relative z-10 flex items-center justify-center overflow-visible ${
            showRequestPopover ? "cursor-pointer" : ""
          }`}
          onMouseEnter={() => (showRequestPopover ? handlePopoverEnter(cell.key) : undefined)}
          onMouseLeave={() => (showRequestPopover ? handlePopoverLeave(cell.key) : undefined)}
        >
          <Image
            data-language-flag
            data-supported-flag={UNCOLORED_COUNTRY_CODES.has(item.country_code) ? "false" : "true"}
            src={item.flag_img_url}
            alt={item.country_name}
            width={48}
            height={32}
            loading="lazy"
            decoding="async"
            className={`relative z-10 h-7 w-10 rounded object-cover ring-1 ring-border/20 sm:h-8 sm:w-12 ${
              showRequestPopover ? "" : "cursor-default"
            }`}
            sizes="48px"
            unoptimized
          />

          {/* Popover temporarily disabled
          {showRequestPopover ? (
            <div
              ref={(node) => {
                popoverRefs.current[cell.key] = node;
              }}
              className="absolute bottom-full left-1/2 z-[35] mb-2 w-52 -translate-x-1/2 translate-y-1 rounded-2xl border border-white/50 bg-white/80 p-3 opacity-0 shadow-[0_10px_32px_rgba(15,23,42,0.14)] backdrop-blur-md transition-colors dark:border-white/10 dark:bg-zinc-950/80 dark:shadow-[0_10px_30px_rgba(0,0,0,0.45)]"
            >
              <p className="text-[11px] leading-snug text-foreground/90">
                <span className="font-semibold">{item.country_name}</span> support is on the roadmap.
              </p>
              <button
                type="button"
                onClick={() => openRequestModal(item.country_name, item.flag_img_url)}
                className="mt-2 inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/20"
              >
                Vote to speed it up <span aria-hidden>→</span>
              </button>
            </div>
          ) : null}
          */}
        </div>
      );
    });

  return (
    <section ref={sectionRef} className="w-full overflow-visible border-t border-border/40 bg-background">
      <div className="mx-auto w-full max-w-7xl overflow-visible container-padding py-24 sm:py-32">
        {/* Section Header */}
        <div className="mx-auto mb-12 max-w-3xl text-center lg:mb-16">
          <h2 className="text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Use Zapify in <span className="gradient-text">your language</span>
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            Create automations, reply to comments, and manage conversations in the language you're comfortable with.
          </p>
        </div>
        <div ref={shellRef} className="relative mx-auto w-full max-w-5xl overflow-visible">
          <div
            ref={planeMoveRef}
            className="pointer-events-none absolute z-[1] h-[min(176px,38vw)] w-[min(160px,35vw)] max-h-[176px] max-w-[160px] will-change-transform sm:h-[176px] sm:w-[160px]"
            aria-hidden
          >
            <Image
              ref={planeImgRef}
              src="/paper_plain.png"
              alt=""
              width={160}
              height={176}
              loading="lazy"
              decoding="async"
              draggable={false}
              className="h-full w-full object-contain will-change-[filter,transform]"
              sizes="160px"
            />
          </div>
          <div className="relative z-20 mx-auto w-full overflow-visible">
            <div
              data-language-grid="phone"
              className="relative z-20 mx-auto w-full overflow-visible lg:hidden"
            >
              <div
                className="grid gap-x-3 gap-y-8 overflow-visible sm:gap-x-3.5 sm:gap-y-10"
                style={{ gridTemplateColumns: `repeat(${GRID_PHONE.cols}, minmax(0, 1fr))` }}
              >
                {mapLanguageCells(gridCellsPhone)}
              </div>
            </div>
            <div
              data-language-grid="desktop"
              className="relative z-20 mx-auto hidden w-full overflow-visible lg:block"
            >
              <div
                className="grid gap-x-3 gap-y-8 overflow-visible sm:gap-x-3.5 sm:gap-y-10"
                style={{ gridTemplateColumns: `repeat(${GRID_DESKTOP.cols}, minmax(0, 1fr))` }}
              >
                {mapLanguageCells(gridCellsDesktop)}
              </div>
            </div>
          </div>

          {/* CTA Button - Centered */}
          <div className="mx-auto mt-16 flex w-full max-w-5xl justify-center px-4 sm:px-6 lg:mt-20 lg:px-8">
            <button
              type="button"
              onClick={() => {
                track("cta_clicked", {
                  cta_id: "CTA_LanguageSupport",
                  button_text: "Start Free in your language",
                });
                openAuth();
              }}
              className={cn(
                "inline-flex h-12 items-center justify-center gap-2 rounded-full bg-primary-gradient px-8 text-base font-bold text-white dark:text-black transition-opacity hover:opacity-90",
                themeColor === "yellow" && "!text-black"
              )}
            >
              Start Free in your language
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {isClientMounted && isRequestModalOpen
        ? createPortal(
            <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/45 px-4 backdrop-blur-sm">
              <div className="w-full max-w-md rounded-3xl border border-white/60 bg-gradient-to-b from-white/95 to-white/85 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.25)] backdrop-blur-xl dark:border-white/10 dark:from-zinc-900/95 dark:to-zinc-950/85 dark:shadow-[0_20px_60px_rgba(0,0,0,0.55)]">
                <div className="mb-4 flex items-start justify-between gap-4">
                  <div className="flex items-start gap-2.5">
                    {requestLanguageFlag ? (
                      <Image
                        src={requestLanguageFlag}
                        alt={requestLanguage}
                        width={32}
                        height={24}
                        className="mt-0.5 h-6 w-8 rounded object-cover ring-1 ring-border/40"
                        unoptimized
                      />
                    ) : null}
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-foreground/60">Language Request</p>
                      <h3 className="text-lg font-semibold text-foreground">Request support for {requestLanguage}</h3>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={closeRequestModal}
                    className="rounded-full p-1.5 text-foreground/60 transition-colors hover:bg-black/5 hover:text-foreground dark:hover:bg-white/10"
                    aria-label="Close modal"
                  >
                    ✕
                  </button>
                </div>

                <label htmlFor="language-request-email" className="mb-2 block text-sm font-medium text-foreground/80">
                  Enter your registered email
                </label>
                <div className="flex items-center gap-2 overflow-hidden">
                  <input
                    id="language-request-email"
                    type="email"
                    value={emailInput}
                    onChange={(event) => setEmailInput(event.target.value)}
                    placeholder="hello@zapify.ai"
                    className={`min-w-0 rounded-xl border border-black/10 bg-white/80 px-3 py-2.5 text-sm text-foreground outline-none transition-all duration-300 focus:border-primary/40 focus:ring-2 focus:ring-primary/20 dark:border-white/15 dark:bg-zinc-900/75 ${
                      hasEmailInput ? "w-[calc(100%-6.5rem)]" : "w-full"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={handleRequestSubmit}
                    className={`w-24 overflow-hidden whitespace-nowrap rounded-xl bg-primary text-sm font-semibold text-primary-foreground shadow-sm transition-all duration-300 ${
                      hasEmailInput
                        ? "pointer-events-auto translate-x-0 px-4 py-2 opacity-100"
                        : "pointer-events-none translate-x-8 px-0 py-2 opacity-0"
                    }`}
                  >
                    Request
                  </button>
                </div>

                {requestFeedback ? (
                  <p
                    className={`mt-2 text-xs ${
                      requestFeedback.startsWith("Request submitted")
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-rose-600 dark:text-rose-400"
                    }`}
                  >
                    {requestFeedback}
                  </p>
                ) : null}
              </div>
            </div>,
            document.body,
          )
        : null}
    </section>
  );
}
